import React, { useState } from 'react';
import { BookOpen, Upload, User as UserIcon, Menu, X } from 'lucide-react';
import Sidebar from './Sidebar';
import { User } from '../types';
import { AnimatePresence, motion } from 'framer-motion';

interface LayoutProps {
  children: React.ReactNode;
  user: User | null;
  onUploadClick: () => void;
  onLogout: () => void;
  logoSrc?: string;
  onOpenProfile?: () => void;
  onHomeClick?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, onUploadClick, onLogout, logoSrc, onOpenProfile, onHomeClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!user) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex bg-[#f8f7f5] text-cool">
      
      {/* Desktop Sidebar */}
      <Sidebar 
        user={user} 
        onLogout={onLogout} 
        logoSrc={logoSrc} 
        onOpenProfile={onOpenProfile || (() => {})} 
        onHomeClick={onHomeClick}
      />

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
              className="fixed inset-0 bg-black/50 z-50 md:hidden backdrop-blur-sm"
            />
            {/* Slide-out Menu */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-80 bg-white z-[51] md:hidden shadow-2xl flex flex-col"
            >
              <div className="flex justify-between items-center p-4 border-b border-warm/10 flex-shrink-0 bg-white">
                <span className="font-serif font-bold text-dark text-lg ml-2">Menu</span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="text-cool hover:text-dark p-2 rounded-full hover:bg-gray-100 transition-colors">
                  <X size={24} />
                </button>
              </div>
              
              {/* Sidebar Container: flex-1 ensures it fills remaining vertical space without overflow/clipping from parent */}
              <div className="flex-1 min-h-0 overflow-hidden relative">
                 <Sidebar 
                    user={user} 
                    onLogout={() => {
                        setIsMobileMenuOpen(false);
                        onLogout();
                    }} 
                    logoSrc={logoSrc} 
                    onOpenProfile={() => {
                        setIsMobileMenuOpen(false);
                        if (onOpenProfile) onOpenProfile();
                    }}
                    isMobile={true} 
                    onLinkClick={() => setIsMobileMenuOpen(false)}
                    onHomeClick={onHomeClick}
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content Area */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen transition-all duration-300">
        
        {/* Mobile Header */}
        <header className="md:hidden h-16 bg-white border-b border-warm/20 flex items-center justify-between px-4 sticky top-0 z-30">
            <div 
                className="flex items-center gap-2 cursor-pointer"
                onClick={onHomeClick}
                aria-label="Go to dashboard"
            >
                {logoSrc ? (
                    <img src={logoSrc} alt="Logo" className="h-8 w-auto max-w-[120px] object-contain" />
                ) : (
                    <>
                        <BookOpen className="h-6 w-6 text-dark" />
                        <span className="font-serif text-lg font-bold text-dark">Esselle Retail</span>
                    </>
                )}
            </div>
            <button 
              className="p-2 text-dark hover:bg-gray-100 rounded-lg transition-colors mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(true)}
            >
                <Menu size={24} />
            </button>
        </header>

        {/* Desktop Header / Action Bar */}
        <header className="hidden md:flex h-20 items-center justify-between px-8 bg-white/50 backdrop-blur-sm sticky top-0 z-30 border-b border-warm/10">
            <div className="font-serif text-xl text-dark">
                {/* Could be breadcrumbs, currently empty or context aware */}
            </div>
            
            <div className="flex items-center gap-4">
                <button 
                    onClick={onUploadClick}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-dark text-white text-sm font-semibold shadow-lg shadow-dark/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 tutorial-upload-btn"
                >
                    <Upload className="w-4 h-4" />
                    <span>Upload PDF</span>
                </button>
            </div>
        </header>

        {/* Page Content */}
        <main className="flex-grow w-full overflow-x-hidden relative">
            {/* Mobile Floating Action Button for Upload */}
            <button 
                onClick={onUploadClick}
                className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-dark text-white rounded-full shadow-xl flex items-center justify-center z-40 hover:scale-105 transition-transform mobile-upload-btn"
            >
                <Upload size={24} />
            </button>
            {children}
        </main>
      </div>

    </div>
  );
};

export default Layout;